// Switching off the student app's learning centre.
//
// Everything an institute publishes reaches students only through the learning
// centre: topic material sits beside that topic's notes, course material is a
// card in its Information section, and customised topic notes replace
// Prepgen's there. Switching it off hides all of that without deleting it, so
// the owner is asked to confirm whenever there is something to hide.

const RESTORED = 'Nothing is deleted: it all comes back when you switch the learning centre on again.';

/** Whether a feature change turns the learning centre off. */
export function isSwitchingOffLearningCenter(key, nextValue) {
  return key === 'learningCenter' && !nextValue;
}

/**
 * What to ask before switching the learning centre off, or null to go ahead.
 *
 * `publishedCount` is how many items the institute has published and
 * `hasCustomNotes` whether it has saved its own topic notes. Either is null
 * when it could not be checked; that asks too, since students might lose
 * material the owner cannot see from here.
 */
export function learningCenterOffConfirmation({ publishedCount, hasCustomNotes }) {
  const countKnown = typeof publishedCount === 'number';
  const notesKnown = typeof hasCustomNotes === 'boolean';
  const hasPublished = countKnown && publishedCount > 0;

  if (countKnown && notesKnown && !hasPublished && !hasCustomNotes) return null;

  const base = { title: 'Switch off the learning centre?', confirmLabel: 'Switch off' };

  if (!hasPublished && !hasCustomNotes) {
    return {
      ...base,
      message:
        "Students see the content you publish and your customised topic notes only in the learning centre, and we couldn't check whether you have any. " +
        `If you switch it off, students will no longer see that material. ${RESTORED}`,
    };
  }

  const parts = [];
  if (hasPublished) {
    parts.push(`the ${publishedCount} ${publishedCount === 1 ? 'item' : 'items'} you've published`);
  }
  if (hasCustomNotes) parts.push('your customised topic notes');
  const pronoun = publishedCount === 1 && !hasCustomNotes ? 'it' : 'them';

  return {
    ...base,
    message:
      `Students see ${parts.join(' and ')} only in the learning centre. ` +
      `If you switch it off, students will no longer see ${pronoun}. ${RESTORED}`,
  };
}
